/** Altura máxima razonable de un renglón; rects más altos suelen ser el bloque entero. */
const ALTO_MAX_LINEA_PX = 40;
const EPSILON = 0.5;

/** Una hoja no puede quedar casi vacía: mínimo ~70% del alto útil (salvo última hoja). */
export const AVANCE_MINIMO_FRAC = 0.7;

/**
 * getBoundingClientRect / getClientRects escalan con zoom del navegador;
 * offsetWidth/scrollHeight no. Normalizamos a px de layout.
 */
export function factorZoomLayout(contenedor) {
  if (!contenedor) return 1;
  const ow = contenedor.offsetWidth;
  if (!ow) return 1;
  const rw = contenedor.getBoundingClientRect().width;
  if (!rw) return 1;
  const z = rw / ow;
  if (!Number.isFinite(z) || z <= 0) return 1;
  if (Math.abs(z - 1) < 0.02) return 1;
  return z;
}

/** Posición Y del elemento relativa al contenedor, en px de layout. */
export function offsetTopRelativo(el, contenedor) {
  if (!el || !contenedor) return 0;
  const zoom = factorZoomLayout(contenedor);
  const cRect = contenedor.getBoundingClientRect();
  const eRect = el.getBoundingClientRect();
  return (eRect.top - cRect.top) / zoom;
}

function fusionarLineasPorTop(lineas) {
  lineas.sort((a, b) => a.top - b.top || a.bottom - b.bottom);

  const fusionadas = [];
  for (const linea of lineas) {
    const ultima = fusionadas[fusionadas.length - 1];
    if (ultima && Math.abs(ultima.top - linea.top) < 4) {
      ultima.top = Math.min(ultima.top, linea.top);
      ultima.bottom = Math.max(ultima.bottom, linea.bottom);
    } else {
      fusionadas.push({ ...linea });
    }
  }

  return fusionadas;
}

function agregarRectLinea(lineas, rect, origenY, zoom) {
  if (rect.height < 0.5 || rect.width < 0.5) return;
  const altoLayout = rect.height / zoom;
  if (altoLayout > ALTO_MAX_LINEA_PX) return;
  lineas.push({
    top: (rect.top - origenY) / zoom,
    bottom: (rect.bottom - origenY) / zoom,
  });
}

/**
 * Una caja por renglón visual.
 * Muestrea caracteres para no perder líneas con <strong> intercalados.
 * Coordenadas en px de layout (independientes del zoom del navegador).
 */
export function lineasDelContenedor(contenedor) {
  const zoom = factorZoomLayout(contenedor);
  const origenY = contenedor.getBoundingClientRect().top;
  const selectores = '[data-contrato-parrafo], p, h1, h2, h3, h4';
  const lineas = [];

  contenedor.querySelectorAll(selectores).forEach((el) => {
    let encontroTexto = false;

    try {
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      let node = walker.nextNode();
      while (node) {
        const text = node.textContent || '';
        if (text.trim()) {
          encontroTexto = true;
          const step = text.length > 80 ? 3 : 1;
          for (let i = 0; i < text.length; i += step) {
            const range = document.createRange();
            const end = Math.min(i + step, text.length);
            range.setStart(node, i);
            range.setEnd(node, end);
            Array.from(range.getClientRects()).forEach((rect) =>
              agregarRectLinea(lineas, rect, origenY, zoom)
            );
          }
        }
        node = walker.nextNode();
      }
    } catch (_) {
      encontroTexto = false;
    }

    if (!encontroTexto) {
      try {
        const range = document.createRange();
        range.selectNodeContents(el);
        Array.from(range.getClientRects()).forEach((rect) =>
          agregarRectLinea(lineas, rect, origenY, zoom)
        );
      } catch (_) {
        Array.from(el.getClientRects()).forEach((rect) =>
          agregarRectLinea(lineas, rect, origenY, zoom)
        );
      }
    }
  });

  return fusionarLineasPorTop(lineas);
}

/**
 * Recorta pageEnd al inicio de cualquier renglón atravesado.
 * Si no hay cruce, lo deja en el hueco tras el último renglón completo.
 */
export function ajustarCorteSinPartirLineas(pageEnd, cursor, lineas) {
  if (!lineas.length) return pageEnd;

  const atravesadas = lineas.filter(
    (l) => l.top < pageEnd - EPSILON && l.bottom > pageEnd + EPSILON
  );

  if (atravesadas.length > 0) {
    const tops = atravesadas
      .map((l) => l.top)
      .filter((top) => top > cursor + EPSILON);
    if (tops.length > 0) {
      return Math.min(...tops);
    }
  }

  // Último renglón que cabe completo; el corte va al inicio del siguiente
  let ultimoCompleto = null;
  for (const l of lineas) {
    if (l.top >= cursor - EPSILON && l.bottom <= pageEnd + EPSILON) {
      ultimoCompleto = l;
    }
  }

  if (ultimoCompleto) {
    const siguiente = lineas.find((l) => l.top > ultimoCompleto.top + EPSILON);
    if (siguiente && siguiente.top < pageEnd + EPSILON) {
      if (siguiente.bottom > pageEnd + EPSILON) {
        return Math.max(siguiente.top, cursor + EPSILON);
      }
    }
  }

  return pageEnd;
}

function ajustarTitulosArticuloHuerfanos(pageEnd, cursor, contenedor) {
  const titulos = contenedor.querySelectorAll('[data-contrato-titulo-articulo]');

  for (const titulo of titulos) {
    const top = offsetTopRelativo(titulo, contenedor);
    const bottom = top + titulo.offsetHeight;

    if (bottom <= cursor || top >= pageEnd) continue;

    const siguiente = titulo.nextElementSibling;
    if (!siguiente) continue;

    const sigTop = offsetTopRelativo(siguiente, contenedor);
    const sigBottom = sigTop + siguiente.offsetHeight;

    if (bottom <= pageEnd + EPSILON && sigBottom > pageEnd + EPSILON && top > cursor) {
      return top;
    }
  }

  return pageEnd;
}

function ajustarBloqueCierreHuerfano(pageEnd, cursor, contenedor) {
  const bloques = contenedor.querySelectorAll('[data-contrato-bloque-cierre]');

  for (const bloque of bloques) {
    const top = offsetTopRelativo(bloque, contenedor);
    const bottom = top + bloque.offsetHeight;

    if (bottom <= cursor || top >= pageEnd) continue;
    if (top >= cursor && bottom <= pageEnd + EPSILON) continue;

    if (top > cursor && top < pageEnd && bottom > pageEnd + EPSILON) {
      return top;
    }
  }

  return pageEnd;
}

function asegurarAvanceSinPartirLineas(pageEnd, cursor, total, altoPaginaPx, lineas) {
  if (pageEnd > cursor + EPSILON) {
    return pageEnd;
  }

  const siguiente = lineas.find((l) => l.top > cursor + EPSILON);
  if (siguiente) {
    const despues = lineas.find((l) => l.top > siguiente.top + EPSILON);
    if (despues) {
      return Math.min(despues.top, total);
    }
    return Math.min(Math.max(siguiente.bottom, cursor + 1), total);
  }

  return Math.min(cursor + altoPaginaPx, total);
}

/**
 * Si las reglas de huérfanos dejaron la hoja casi vacía, forzar un corte
 * cerca del final de página (sin partir líneas). Nunca una hoja con 1 renglón.
 */
export function asegurarLlenadoMinimoPagina({
  pageEnd,
  cursor,
  altoPaginaPx,
  totalEfectivo,
  lineas,
}) {
  const restante = totalEfectivo - cursor;
  // Última hoja (o menos de una hoja de contenido): no forzar
  if (restante <= altoPaginaPx + EPSILON) {
    return Math.min(pageEnd, totalEfectivo);
  }

  const avanceMinimo = altoPaginaPx * AVANCE_MINIMO_FRAC;
  if (pageEnd - cursor >= avanceMinimo - EPSILON) {
    return pageEnd;
  }

  let forzado = Math.min(cursor + altoPaginaPx, totalEfectivo);
  forzado = ajustarCorteSinPartirLineas(forzado, cursor, lineas);

  if (forzado - cursor < avanceMinimo * 0.85) {
    // Último recurso: llenar la hoja; el refinamiento por canvas evita partir glifos
    forzado = Math.min(cursor + altoPaginaPx, totalEfectivo);
  }

  return forzado;
}

/**
 * Llena cada hoja A4 al máximo sin cortar líneas de texto a la mitad.
 */
export function calcularSaltosPaginaContrato(contenedor, altoPaginaPx) {
  const total = contenedor?.scrollHeight || 0;
  if (!contenedor || total <= 0) return [0];
  if (total <= altoPaginaPx) return [0];

  const lineas = lineasDelContenedor(contenedor);
  const ultimaLineaBottom = lineas.length
    ? Math.max(...lineas.map((l) => l.bottom))
    : total;
  // No paginar más allá del último renglón con texto (evita hoja en blanco)
  const totalEfectivo = Math.min(total, Math.ceil(ultimaLineaBottom + 8));

  if (totalEfectivo <= altoPaginaPx) return [0];

  const saltos = [0];
  let cursor = 0;
  const maxPaginas = Math.max(
    50,
    Math.ceil(totalEfectivo / Math.max(altoPaginaPx * 0.25, 1)) + 5
  );

  while (cursor + altoPaginaPx < totalEfectivo - EPSILON && saltos.length < maxPaginas) {
    let pageEnd = cursor + altoPaginaPx;
    pageEnd = ajustarCorteSinPartirLineas(pageEnd, cursor, lineas);
    pageEnd = ajustarTitulosArticuloHuerfanos(pageEnd, cursor, contenedor);
    pageEnd = ajustarBloqueCierreHuerfano(pageEnd, cursor, contenedor);
    pageEnd = ajustarCorteSinPartirLineas(pageEnd, cursor, lineas);

    // Huérfanos solo si la hoja ya está suficientemente llena
    const avanceMinimo = altoPaginaPx * AVANCE_MINIMO_FRAC;
    if (pageEnd - cursor < avanceMinimo) {
      const intento = Math.min(cursor + altoPaginaPx, totalEfectivo);
      let pageEndLleno = ajustarCorteSinPartirLineas(intento, cursor, lineas);
      // No reaplicar título/cierre si vacían la hoja
      if (pageEndLleno - cursor >= avanceMinimo) {
        const conHuerfanos = ajustarBloqueCierreHuerfano(
          ajustarTitulosArticuloHuerfanos(pageEndLleno, cursor, contenedor),
          cursor,
          contenedor
        );
        pageEnd =
          conHuerfanos - cursor >= avanceMinimo
            ? ajustarCorteSinPartirLineas(conHuerfanos, cursor, lineas)
            : pageEndLleno;
      } else {
        pageEnd = pageEndLleno;
      }
    }

    pageEnd = asegurarAvanceSinPartirLineas(
      pageEnd,
      cursor,
      totalEfectivo,
      altoPaginaPx,
      lineas
    );

    pageEnd = asegurarLlenadoMinimoPagina({
      pageEnd,
      cursor,
      altoPaginaPx,
      totalEfectivo,
      lineas,
    });

    cursor = Math.floor(pageEnd);
    if (cursor <= saltos[saltos.length - 1] || cursor >= totalEfectivo) {
      break;
    }
    saltos.push(cursor);
  }

  // Quitar saltos finales sin ningún renglón de texto (hoja en blanco)
  while (saltos.length > 1) {
    const ultimo = saltos[saltos.length - 1];
    const hayTexto = lineas.some((l) => l.bottom > ultimo + EPSILON);
    if (hayTexto) break;
    saltos.pop();
  }

  return saltos;
}

/** Altura del recorte en una hoja (evita solapar con la página siguiente). */
export function alturaContenidoPagina(saltos, indice, altoPaginaPx, totalAltura) {
  const inicio = saltos[indice] ?? 0;
  const siguiente = saltos[indice + 1];
  if (siguiente != null) {
    return Math.max(0, Math.min(altoPaginaPx, siguiente - inicio));
  }
  return Math.max(0, Math.min(altoPaginaPx, totalAltura - inicio));
}
